import { DEFAULT_ORG_SETTINGS } from "./organizationSettings";
import {
  PAYROLL_WEEK_STARTS_ON,
  normalizePayrollWeekStartsOn,
} from "./payrollWeek";
import { computeEmployeePayrollTotals } from "./payrollOvertime";

export const PAYROLL_WEEK_START_OPTIONS = Object.freeze([
  { value: PAYROLL_WEEK_STARTS_ON.SUNDAY, label: "Sunday" },
  { value: PAYROLL_WEEK_STARTS_ON.MONDAY, label: "Monday" },
]);

export const PAYROLL_SETTINGS_FORM_DEFAULTS = Object.freeze({
  weekStartsOn: PAYROLL_WEEK_STARTS_ON.SUNDAY,
  weeklyOvertimeEnabled: true,
  thresholdHours: 40,
  multiplier: 1.5,
});

const EXAMPLE_HOURS = 43;
const EXAMPLE_RATE = 20;

export function formatPayrollWeekStartsOnLabel(weekStartsOn) {
  const normalized = normalizePayrollWeekStartsOn(weekStartsOn);
  return (
    PAYROLL_WEEK_START_OPTIONS.find((option) => option.value === normalized)
      ?.label || "Sunday"
  );
}

export function payrollSettingsFormFromSettings(settings) {
  const payroll = settings?.payroll || DEFAULT_ORG_SETTINGS.payroll;

  return {
    weekStartsOn: normalizePayrollWeekStartsOn(payroll.week_starts_on),
    weeklyOvertimeEnabled: payroll.weekly_overtime?.enabled !== false,
    thresholdHours: Number(
      payroll.weekly_overtime?.threshold_hours ??
        DEFAULT_ORG_SETTINGS.payroll.weekly_overtime.threshold_hours,
    ),
    multiplier: Number(
      payroll.weekly_overtime?.multiplier ??
        DEFAULT_ORG_SETTINGS.payroll.weekly_overtime.multiplier,
    ),
  };
}

export function normalizePayrollSettingsThresholdHours(value) {
  const parsed = Number(String(value ?? "").trim());

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
}

export function normalizePayrollSettingsMultiplier(value) {
  const parsed = Number(String(value ?? "").trim());

  if (!Number.isFinite(parsed) || parsed < 1) {
    return null;
  }

  return parsed;
}

export function validatePayrollSettingsForm(form) {
  const errors = {};
  const thresholdHours = normalizePayrollSettingsThresholdHours(
    form.thresholdHours,
  );
  const multiplier = normalizePayrollSettingsMultiplier(form.multiplier);

  if (thresholdHours == null) {
    errors.thresholdHours = "Threshold must be a number greater than zero.";
  }

  if (multiplier == null) {
    errors.multiplier = "Multiplier must be a number of at least 1.0.";
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
    normalized: {
      weekStartsOn: normalizePayrollWeekStartsOn(form.weekStartsOn),
      weeklyOvertimeEnabled: form.weeklyOvertimeEnabled !== false,
      thresholdHours: thresholdHours ?? PAYROLL_SETTINGS_FORM_DEFAULTS.thresholdHours,
      multiplier: multiplier ?? PAYROLL_SETTINGS_FORM_DEFAULTS.multiplier,
    },
  };
}

export function payrollSettingsFormToPatch(form) {
  const { normalized } = validatePayrollSettingsForm(form);

  return {
    payroll: {
      week_starts_on: normalized.weekStartsOn,
      weekly_overtime: {
        enabled: normalized.weeklyOvertimeEnabled,
        threshold_hours: normalized.thresholdHours,
        multiplier: normalized.multiplier,
      },
    },
  };
}

export function buildPayrollSettingsExample(form) {
  const { normalized } = validatePayrollSettingsForm(form);
  const totals = computeEmployeePayrollTotals(
    [{ total_hours: EXAMPLE_HOURS, hourly_rate: EXAMPLE_RATE }],
    {
      enabled: normalized.weeklyOvertimeEnabled,
      threshold_hours: normalized.thresholdHours,
      multiplier: normalized.multiplier,
    },
  );

  return {
    weekStartsOnLabel: formatPayrollWeekStartsOnLabel(normalized.weekStartsOn),
    weeklyOvertimeEnabled: normalized.weeklyOvertimeEnabled,
    thresholdHours: normalized.thresholdHours,
    multiplier: normalized.multiplier,
    exampleHours: EXAMPLE_HOURS,
    exampleRate: EXAMPLE_RATE,
    regularHours: totals.regularHours,
    overtimeHours: totals.overtimeHours,
    grossPay: totals.grossPay,
  };
}
